'use client';

import { usePathname, useRouter } from 'next/navigation';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import {
  LOCALE_COOKIE,
  LOCALE_COOKIE_MAX_AGE,
  localeMeta,
  type Locale,
} from '@/i18n/config';
import { swapLocale } from '@/i18n/routing';
import { useLocale } from '@/i18n/useLocale';
import { cn } from '@/lib/cn';

/**
 * The language pass.
 *
 * Switching language re-typesets the entire page, so the transition says
 * exactly that: a single hairline of brand light sweeps down the viewport,
 * laying a navy veil behind it; the route changes underneath; then the sweep
 * continues on down and takes the veil with it, uncovering the page in its new
 * language. One continuous downward pass — a plotter crossing a sheet, not a
 * curtain going up and down.
 *
 * Sibling to `OrderTransition`, deliberately: same provider shape, same
 * `--ease-out-quart`, same navy. The gesture differs because the meaning does —
 * the order CTA is a ceremony that carries you somewhere, this is a machine
 * re-setting the page you are already on.
 *
 * What the veil shows needs no translation, which is why it can be shown at
 * all: the wordmark, the two locale codes with the travel between them, and the
 * target language's own name for itself.
 *
 * The scroll position is kept (`scroll: false`), so the reader is uncovered
 * exactly where they were reading. Under `prefers-reduced-motion` there is no
 * overlay at all — just the navigation.
 */

/* Cover = veil wipes down over the page; hold = the new route settles
 * underneath it; reveal = the sweep carries the veil off the bottom. */
const COVER_MS = 460;
const HOLD_MS = 220;
const REVEAL_MS = 560;

/** A navigation that never lands must not leave the page covered. */
const BAIL_MS = 4000;

type Phase = 'idle' | 'cover' | 'reveal';

/** Which codes the veil shows while it is up. */
type Pass = { from: Locale; to: Locale };

const LanguagePassContext = createContext<((next: Locale) => void) | null>(null);

/** Null when no provider is mounted — callers fall back to a plain link. */
export function useLanguagePass() {
  return useContext(LanguagePassContext);
}

/**
 * Remembers a deliberate choice so `/` stops guessing from `Accept-Language`.
 * Written from the client because the switcher is a link, not a form: a Server
 * Action here would turn one navigation into two round trips.
 */
function rememberLocale(locale: Locale) {
  const secure = window.location.protocol === 'https:' ? '; Secure' : '';
  document.cookie =
    `${LOCALE_COOKIE}=${locale}; Path=/; Max-Age=${LOCALE_COOKIE_MAX_AGE}` +
    `; SameSite=Lax${secure}`;
}

export function LanguagePassProvider({ children }: { children: ReactNode }) {
  const router = useRouter();
  const pathname = usePathname();
  const current = useLocale();

  const [phase, setPhase] = useState<Phase>('idle');
  const [pass, setPass] = useState<Pass | null>(null);
  /** The locale we are waiting to see in the URL. */
  const awaiting = useRef<Locale | null>(null);

  const start = useCallback(
    (next: Locale) => {
      if (next === current || awaiting.current) return;

      rememberLocale(next);
      const target = swapLocale(pathname, next);

      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        router.push(target, { scroll: false });
        return;
      }

      setPass({ from: current, to: next });
      setPhase('cover');
      awaiting.current = next;
      window.setTimeout(
        () => router.push(target, { scroll: false }),
        COVER_MS,
      );
    },
    [current, pathname, router],
  );

  // The new language is in the URL and painting under the veil: give it a beat
  // to settle, then let the sweep carry the veil off.
  useEffect(() => {
    if (awaiting.current === null || awaiting.current !== current) return;
    awaiting.current = null;

    const lift = window.setTimeout(() => setPhase('reveal'), HOLD_MS);
    const settle = window.setTimeout(() => {
      setPhase('idle');
      setPass(null);
    }, HOLD_MS + REVEAL_MS);

    return () => {
      window.clearTimeout(lift);
      window.clearTimeout(settle);
    };
  }, [current]);

  // Safety net for a navigation that never arrives.
  useEffect(() => {
    if (phase !== 'cover') return;
    const bail = window.setTimeout(() => {
      if (awaiting.current !== null) {
        awaiting.current = null;
        setPhase('idle');
        setPass(null);
      }
    }, BAIL_MS);
    return () => window.clearTimeout(bail);
  }, [phase]);

  return (
    <LanguagePassContext.Provider value={start}>
      {children}

      {/* Purely visual: the switcher's own link announces the change, and the
          page that arrives is the real result. */}
      <div
        aria-hidden="true"
        className={cn(
          'lang-pass',
          phase === 'cover' && 'is-cover',
          phase === 'reveal' && 'is-reveal',
        )}
      >
        <div className="lang-pass__veil">
          <div aria-hidden="true" className="absolute inset-0 grid-bg" />
          <div aria-hidden="true" className="lang-pass__glow" />

          <div className="lang-pass__stage">
            <span className="label-xs text-ondark-faint">MPMG SOFT</span>

            <p className="lang-pass__codes">
              <span className="lang-pass__from">
                {pass ? localeMeta[pass.from].code : ''}
              </span>
              <span aria-hidden="true" className="lang-pass__travel">
                <span className="lang-pass__travel-bar" />
              </span>
              <span className="lang-pass__to">
                {pass ? localeMeta[pass.to].code : ''}
              </span>
            </p>

            <p className="lang-pass__endonym">
              {pass ? localeMeta[pass.to].endonym : ''}
            </p>
          </div>
        </div>

        {/* Outside the veil, so the clip that reveals the veil cannot clip the
            edge that is drawing it. */}
        <span className="lang-pass__scan" />
      </div>
    </LanguagePassContext.Provider>
  );
}
